import React from 'react';
import Icon from '../../../components/AppIcon';

const WidgetPerformanceMatrix = ({ data }) => {
  const getPerformanceStatus = (loadTime, errorRate) => {
    if (loadTime < 1000 && errorRate < 1) return { label: 'Excellent', color: 'text-success', bg: 'bg-success/10' };
    if (loadTime < 1500 && errorRate < 2) return { label: 'Good', color: 'text-accent', bg: 'bg-accent/10' };
    if (loadTime < 2000 && errorRate < 3) return { label: 'Fair', color: 'text-warning', bg: 'bg-warning/10' };
    return { label: 'Poor', color: 'text-error', bg: 'bg-error/10' };
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <div className="mb-6">
        <h3 className="font-heading font-semibold text-base md:text-lg text-foreground mb-1">
          Widget Performance Matrix
        </h3>
        <p className="caption text-xs md:text-sm text-muted-foreground">
          Load times, error rates, and user satisfaction
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-2 caption text-xs font-medium text-muted-foreground">
                Widget
              </th>
              <th className="text-center py-3 px-2 caption text-xs font-medium text-muted-foreground">
                Load Time
              </th>
              <th className="text-center py-3 px-2 caption text-xs font-medium text-muted-foreground">
                Error Rate
              </th>
              <th className="text-center py-3 px-2 caption text-xs font-medium text-muted-foreground">
                Satisfaction
              </th>
              <th className="text-center py-3 px-2 caption text-xs font-medium text-muted-foreground">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {data?.slice(0, 8)?.map((widget, index) => {
              const status = getPerformanceStatus(widget?.loadTime, parseFloat(widget?.errorRate));
              return (
                <tr key={index} className="border-b border-border/50 hover:bg-muted/30 transition-smooth">
                  <td className="py-3 px-2">
                    <div className="flex items-center gap-2">
                      <Icon name="Layout" size={14} color="var(--color-muted-foreground)" />
                      <span className="font-heading text-xs font-medium text-foreground truncate max-w-[120px]">
                        {widget?.widgetName}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-2 text-center">
                    <span className="data-text text-xs font-medium text-foreground">
                      {widget?.loadTime}ms
                    </span>
                  </td>
                  <td className="py-3 px-2 text-center">
                    <span className="data-text text-xs font-medium text-foreground">
                      {widget?.errorRate}%
                    </span>
                  </td>
                  <td className="py-3 px-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Icon name="Star" size={12} color="var(--color-warning)" />
                      <span className="data-text text-xs font-medium text-foreground">
                        {widget?.satisfactionScore}%
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-2 text-center">
                    <span className={`caption text-xs font-medium ${status?.color} px-2 py-1 rounded ${status?.bg}`}>
                      {status?.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {data?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="BarChart" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-50" />
          <p className="caption text-sm text-muted-foreground">No performance data available</p>
        </div>
      )}

      <div className="mt-6 pt-4 border-t border-border">
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="flex items-center gap-2">
            <Icon name="Zap" size={14} color="var(--color-success)" />
            <span className="caption text-muted-foreground">Avg Load: {(data?.reduce((sum, w) => sum + w?.loadTime, 0) / (data?.length || 1))?.toFixed(0)}ms</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="AlertCircle" size={14} color="var(--color-error)" />
            <span className="caption text-muted-foreground">Avg Error: {(data?.reduce((sum, w) => sum + parseFloat(w?.errorRate), 0) / (data?.length || 1))?.toFixed(2)}%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WidgetPerformanceMatrix;